/**
 * Theme charging sims — REAL stable-fluids simulation (the classic
 * GPU Navier-Stokes: semi-Lagrangian advection, vorticity
 * confinement, Jacobi pressure solve, dye transport — the same
 * pipeline as the well-known WebGL-Fluid-Simulation / marbling
 * demos, written self-contained here).
 *
 * Two personalities, selected by variant:
 *
 *   'fluid-initiative-dark' / 'fluid-initiative-light' — Initiative.
 *     Touch/drag stirs the fluid exactly like the reference fluid
 *     sim; when idle, the sim breathes on its own with pulsing
 *     brand-green light bursts (timed splats whose cadence rides the
 *     live charge power). Dye fades at a medium rate so bursts bloom
 *     and dissolve.
 *
 *   'fluid-marbling-light' — Electric Light.
 *     White paper, graphite/chrome inks, VERY low dye dissipation so
 *     motion leaves persistent marbled trails. While charging, pulse
 *     emitters sweep across the surface leaving combed trails; the
 *     finger drags ink exactly like the marbling reference; left
 *     alone it settles back to calm paper as the trails slowly lift.
 *
 *   'fluid-electric-dark' — Dark Electric.
 *     The reference WebGL-Fluid-Simulation feel in the OLED identity:
 *     silver/white/grey dye blooming on pure black with strong swirl
 *     and a lively bloom-and-fade (medium dissipation — motion reads
 *     immediately, then breathes away). Idle pulses are silver light
 *     bursts riding the charge power; the finger throws big bright
 *     splats exactly like the demo. Same engine as Electric Light,
 *     deliberately different fluidity: fast and luminous vs slow and
 *     inky.
 *
 * Units: velocity is stored in uv/second, so advection is simply
 * `uv - dt * velocity`. Splat radii are in uv-space against the
 * aspect-corrected distance. All targets are square (the charging
 * canvas is square), so aspect is 1 — the uAspect uniform stays in
 * the shaders anyway to keep the math honest if a future surface
 * isn't square.
 */

import { getOrCreateProgram } from '../glContext.js';
import {
  SIM_VERTEX_SOURCE,
  blitTo,
  bindTexture,
  createSimDoubleTarget,
  createSimTarget,
  disposeSimDoubleTarget,
  disposeSimTarget,
  resolveSimFormats,
  setupSimQuad,
  type SimDoubleTarget,
  type SimTarget,
} from './simGl.js';

export type FluidVariantId =
  | 'fluid-initiative-dark'
  | 'fluid-initiative-light'
  | 'fluid-marbling-light'
  | 'fluid-electric-dark'
  | 'fluid-cartographic-light' | 'fluid-cartographic-dark'
  | 'fluid-midnight-light' | 'fluid-midnight-dark';

// ── Fragment sources ─────────────────────────────────────────────────

const ADVECTION_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uVelocity;
uniform sampler2D uSource;
uniform float uDt;
uniform float uDissipation;
in vec2 vUv;
out vec4 frag;
void main() {
  vec2 coord = vUv - uDt * texture(uVelocity, vUv).xy;
  frag = uDissipation * texture(uSource, coord);
  frag.a = 1.0;
}`;

const DIVERGENCE_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uVelocity;
uniform vec2 uTexelSize;
in vec2 vUv;
out vec4 frag;
void main() {
  float L = texture(uVelocity, vUv - vec2(uTexelSize.x, 0.0)).x;
  float R = texture(uVelocity, vUv + vec2(uTexelSize.x, 0.0)).x;
  float B = texture(uVelocity, vUv - vec2(0.0, uTexelSize.y)).y;
  float T = texture(uVelocity, vUv + vec2(0.0, uTexelSize.y)).y;
  float div = 0.5 * (R - L + T - B);
  frag = vec4(div, 0.0, 0.0, 1.0);
}`;

const CURL_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uVelocity;
uniform vec2 uTexelSize;
in vec2 vUv;
out vec4 frag;
void main() {
  float L = texture(uVelocity, vUv - vec2(uTexelSize.x, 0.0)).y;
  float R = texture(uVelocity, vUv + vec2(uTexelSize.x, 0.0)).y;
  float B = texture(uVelocity, vUv - vec2(0.0, uTexelSize.y)).x;
  float T = texture(uVelocity, vUv + vec2(0.0, uTexelSize.y)).x;
  float vorticity = 0.5 * (R - L - T + B);
  frag = vec4(vorticity, 0.0, 0.0, 1.0);
}`;

const VORTICITY_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uVelocity;
uniform sampler2D uCurl;
uniform vec2 uTexelSize;
uniform float uCurlStrength;
uniform float uDt;
uniform float uVelocityCap;
in vec2 vUv;
out vec4 frag;
void main() {
  float L = texture(uCurl, vUv - vec2(uTexelSize.x, 0.0)).x;
  float R = texture(uCurl, vUv + vec2(uTexelSize.x, 0.0)).x;
  float B = texture(uCurl, vUv - vec2(0.0, uTexelSize.y)).x;
  float T = texture(uCurl, vUv + vec2(0.0, uTexelSize.y)).x;
  float C = texture(uCurl, vUv).x;
  vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
  force /= length(force) + 0.0001;
  force *= uCurlStrength * C;
  force.y *= -1.0;
  vec2 velocity = texture(uVelocity, vUv).xy;
  velocity += force * uDt;
  velocity = clamp(velocity, vec2(-uVelocityCap), vec2(uVelocityCap));
  frag = vec4(velocity, 0.0, 1.0);
}`;

const PRESSURE_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uPressure;
uniform sampler2D uDivergence;
uniform vec2 uTexelSize;
in vec2 vUv;
out vec4 frag;
void main() {
  float L = texture(uPressure, vUv - vec2(uTexelSize.x, 0.0)).x;
  float R = texture(uPressure, vUv + vec2(uTexelSize.x, 0.0)).x;
  float B = texture(uPressure, vUv - vec2(0.0, uTexelSize.y)).x;
  float T = texture(uPressure, vUv + vec2(0.0, uTexelSize.y)).x;
  float divergence = texture(uDivergence, vUv).x;
  float pressure = (L + R + B + T - divergence) * 0.25;
  frag = vec4(pressure, 0.0, 0.0, 1.0);
}`;

const GRADIENT_SUBTRACT_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uPressure;
uniform sampler2D uVelocity;
uniform vec2 uTexelSize;
in vec2 vUv;
out vec4 frag;
void main() {
  float L = texture(uPressure, vUv - vec2(uTexelSize.x, 0.0)).x;
  float R = texture(uPressure, vUv + vec2(uTexelSize.x, 0.0)).x;
  float B = texture(uPressure, vUv - vec2(0.0, uTexelSize.y)).x;
  float T = texture(uPressure, vUv + vec2(0.0, uTexelSize.y)).x;
  vec2 velocity = texture(uVelocity, vUv).xy;
  velocity -= 0.5 * vec2(R - L, T - B);
  frag = vec4(velocity, 0.0, 1.0);
}`;

const SPLAT_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uTarget;
uniform float uAspect;
uniform vec2 uPoint;
uniform vec3 uColor;
uniform float uRadius;
in vec2 vUv;
out vec4 frag;
void main() {
  vec2 p = vUv - uPoint;
  p.x *= uAspect;
  float d = exp(-dot(p, p) / uRadius);
  vec3 base = texture(uTarget, vUv).xyz;
  frag = vec4(base + uColor * d, 1.0);
}`;

/**
 * Display pass. Two colour treatments, compile-time selected:
 *   ADDITIVE (initiative): dye glows on the theme background — the
 *     dye texture holds emitted light.
 *   SUBTRACTIVE (marbling): dye is INK on paper — each channel
 *     darkens the paper toward that ink's colour (classic marbling
 *     read on a pale ground).
 * A soft radial vignette keeps the square canvas edges quiet inside
 * the dispatcher's circular-ish glow frame.
 */
const DISPLAY_FRAG_TEMPLATE = `#version 300 es
precision highp float;
uniform sampler2D uDye;
uniform vec3 uBg;
uniform float uVignette;
uniform float uTime;
uniform float uPersonality;
uniform float uLight;
in vec2 vUv;
out vec4 frag;
float terrainHash(vec2 p) {
  p=fract(p*vec2(123.34,345.45));
  p+=dot(p,p+34.345);
  return fract(p.x*p.y);
}
float terrainNoise(vec2 p) {
  vec2 i=floor(p),f=fract(p);
  f=f*f*(3.0-2.0*f);
  return mix(mix(terrainHash(i),terrainHash(i+vec2(1,0)),f.x),
             mix(terrainHash(i+vec2(0,1)),terrainHash(i+vec2(1,1)),f.x),f.y);
}
float terrainHeight(vec2 p) {
  float height=terrainNoise(p)*0.58;
  p=mat2(0.8,-0.6,0.6,0.8)*p*2.03+17.2;
  height+=terrainNoise(p)*0.28;
  return height+terrainNoise(p*2.07+8.4)*0.14;
}
float contour(float height,float levels) {
  float h=height*levels;
  float width=max(fwidth(h),0.018);
  return 1.0-smoothstep(width*0.45,width*1.45,abs(fract(h+0.5)-0.5));
}
void main() {
  vec3 dye = texture(uDye, vUv).rgb;
  #ifdef DISPLAY_SUBTRACTIVE
  // Ink density per channel soaks the paper. The max() keeps
  // repeated over-splatting from inverting into neon negatives.
  vec3 col = uBg * (vec3(1.0) - clamp(dye, 0.0, 1.0) * 0.92);
  #else
  // Emitted light over the ground, gently tone-mapped so stacked
  // bursts saturate instead of clipping.
  vec3 col = uBg + dye / (vec3(1.0) + dye * 0.6);
  #endif
  vec2 c = vUv - 0.5;
  if(uPersonality>0.5 && uPersonality<1.5) {
    // Survey terrain fills the square. The fluid warps local elevations
    // under a finger; there is no radial or spiral coordinate system.
    vec3 ink=dye/(vec3(1.0)+dye);
    vec2 survey=vUv*3.6+vec2(uTime*0.022,-uTime*0.013);
    survey+=vec2(ink.r-ink.g,ink.g-ink.b)*0.42;
    float height=terrainHeight(survey)+dot(ink,vec3(0.035,0.085,0.025));
    vec3 forest=mix(vec3(0.035,0.12,0.09),vec3(0.78,0.83,0.65),uLight);
    vec3 ridge=mix(vec3(0.26,0.39,0.14),vec3(0.98,0.95,0.82),uLight);
    col=mix(forest,ridge,smoothstep(0.18,0.83,height));
    float minor=contour(height,28.0),major=contour(height,7.0);
    vec3 line=mix(vec3(0.32,0.67,0.39),vec3(0.26,0.43,0.22),uLight);
    col=mix(col,line,minor*0.46);
    float pulse=pow(0.5+0.5*sin(height*52.0-uTime*1.6),12.0);
    vec3 gold=mix(vec3(0.80,0.89,0.31),vec3(0.58,0.48,0.13),uLight);
    col=mix(col,mix(line,gold,pulse),major*0.88);
    col+=mix(vec3(0.13,0.20,0.03),vec3(-0.055,-0.02,-0.045),uLight)*dot(ink,vec3(0.4));
    // Faint survey grid and a moving energy path echo a map, not a globe.
    vec2 gridDistance=abs(fract(vUv*8.0+0.5)-0.5);
    float grid=1.0-smoothstep(0.006,0.018,min(gridDistance.x,gridDistance.y));
    col=mix(col,line,grid*0.09);
    float routeY=0.37+sin(vUv.x*5.0+0.3)*0.13+terrainNoise(vec2(vUv.x*4.0,3.1))*0.16;
    float route=exp(-pow((vUv.y-routeY-ink.g*0.07)/0.004,2.0));
    float tracer=exp(-pow((vUv.x-fract(uTime*0.09))/0.045,2.0));
    col=mix(col,gold,route*(0.22+tracer*0.78));
  } else if(uPersonality>1.5) {
    // Luminous sparks pulse independently; dragging carries their blue plasma.
    for(int i=0;i<18;i++) {
      float fi=float(i);
      vec2 star=vec2(fract(sin(fi*81.7+1.0)*4375.2),fract(cos(fi*31.3)*1738.4));
      star+=vec2(sin(uTime*0.31+fi),cos(uTime*0.29+fi))*0.024;
      float d=length(vUv-star),pulse=pow(0.5+0.5*sin(uTime*2.0+fi*1.7),4.0);
      float spark=exp(-d*d/0.000024)+exp(-d*110.0)*0.12;
      col+=mix(vec3(0.35,0.58,1.0),vec3(-0.10,-0.17,-0.04),uLight)*spark*pulse;
    }
  }
  float v = 1.0-smoothstep(0.46, 0.78, length(c));
  col = mix(uBg, col, mix(1.0, v, uVignette));
  frag = vec4(col, 1.0);
}`;

// ── Variant tuning ───────────────────────────────────────────────────

interface FluidVariantConfig {
  displayMode: 'additive' | 'subtractive';
  /** Display ground colour (matches the theme surface tokens). */
  bg: readonly [number, number, number];
  /** Dye fade per second toward 0 (1 = never fades). */
  dyeDissipation: number;
  velocityDissipation: number;
  curlStrength: number;
  pressureIterations: number;
  /** Splat radius for finger drags (uv-space, pre-squared scale). */
  splatRadius: number;
  /** How hard a finger drag pushes velocity (multiplier on uv/s). */
  splatForce: number;
  /** Idle/charging emitter personality. */
  emitter: 'bursts' | 'sweeps' | 'terrain';
  /** Seconds between emitter events at baseline intensity. */
  emitPeriodSec: number;
  /** Dye colours the emitter cycles through (linear-ish RGB). */
  inks: ReadonlyArray<readonly [number, number, number]>;
  /** Ink colour for finger drags. */
  touchInk: readonly [number, number, number];
  /** 0..1 vignette strength in the display pass. */
  vignette: number;
  personality?: 1 | 2;
}

/** EVI brand greens (see packages/skins default variants) as linear-ish
 *  RGB triples for the dye. Slight hue spread so consecutive bursts
 *  read as living light rather than a single stamp. */
const INITIATIVE_INKS: ReadonlyArray<readonly [number, number, number]> = [
  [0.13, 0.77, 0.37], // #22C55E
  [0.20, 0.83, 0.60], // #34D399
  [0.02, 0.59, 0.41], // #059669
  [0.45, 0.95, 0.55], // bright pulse highlight
];

const FLUID_VARIANTS: Record<FluidVariantId, FluidVariantConfig> = {
  'fluid-cartographic-dark': {
    displayMode:'additive', bg:[0.055,0.075,0.035], dyeDissipation:0.80,
    velocityDissipation:0.72,curlStrength:5,pressureIterations:18,
    splatRadius:0.0032,splatForce:6.5,emitter:'terrain',emitPeriodSec:1.1,
    inks:[[0.20,0.66,0.10],[0.65,0.70,0.08],[0.05,0.39,0.20]],
    touchInk:[0.58,0.77,0.14],vignette:0.08,personality:1,
  },
  'fluid-cartographic-light': {
    displayMode:'subtractive',bg:[0.97,0.95,0.87],dyeDissipation:0.80,
    velocityDissipation:0.72,curlStrength:5,pressureIterations:18,
    splatRadius:0.0032,splatForce:6.5,emitter:'terrain',emitPeriodSec:1.1,
    inks:[[0.80,0.33,0.88],[0.31,0.30,0.92],[0.87,0.62,0.81]],
    touchInk:[0.70,0.31,0.92],vignette:0.08,personality:1,
  },
  'fluid-midnight-dark': {
    displayMode:'additive',bg:[0.015,0.025,0.08],dyeDissipation:0.66,
    velocityDissipation:0.86,curlStrength:27,pressureIterations:18,
    splatRadius:0.0028,splatForce:6.5,emitter:'bursts',emitPeriodSec:1.05,
    inks:[[0.07,0.30,0.90],[0.09,0.63,0.91],[0.42,0.12,0.75]],
    touchInk:[0.17,0.57,1.0],vignette:0.8,personality:2,
  },
  'fluid-midnight-light': {
    displayMode:'subtractive',bg:[0.91,0.95,1.0],dyeDissipation:0.66,
    velocityDissipation:0.86,curlStrength:27,pressureIterations:18,
    splatRadius:0.0028,splatForce:6.5,emitter:'bursts',emitPeriodSec:1.05,
    inks:[[0.87,0.62,0.06],[0.86,0.35,0.10],[0.53,0.83,0.15]],
    touchInk:[0.81,0.44,0.02],vignette:0.7,personality:2,
  },
  'fluid-initiative-dark': {
    displayMode: 'additive',
    bg: [0.023, 0.024, 0.027],
    dyeDissipation: 0.90,
    velocityDissipation: 0.55,
    curlStrength: 14,
    pressureIterations: 20,
    splatRadius: 0.0022,
    splatForce: 5.5,
    emitter: 'bursts',
    emitPeriodSec: 1.5,
    inks: INITIATIVE_INKS,
    touchInk: [0.13, 0.77, 0.37],
    vignette: 0.85,
  },
  'fluid-initiative-light': {
    // Light mode: the same living-green identity over the cream
    // surface. Additive still reads correctly because the greens are
    // darker than pure light — they bloom against the paler ground.
    displayMode: 'subtractive',
    bg: [0.985, 0.985, 0.975],
    dyeDissipation: 0.90,
    velocityDissipation: 0.55,
    curlStrength: 14,
    pressureIterations: 20,
    splatRadius: 0.0022,
    splatForce: 5.5,
    emitter: 'bursts',
    emitPeriodSec: 1.5,
    // Subtractive inks: channel weights chosen so paper - ink lands
    // on the brand greens (ink absorbs red+blue, keeps green).
    inks: [
      [0.86, 0.23, 0.63], // -> ~#22C55E on white
      [0.80, 0.17, 0.40], // -> ~#34D399
      [0.98, 0.41, 0.59], // -> ~#059669 (deep)
    ],
    touchInk: [0.86, 0.23, 0.63],
    vignette: 0.7,
  },
  'fluid-electric-dark': {
    displayMode: 'additive',
    // Pure-black OLED ground (the Dark Electric identity).
    bg: [0.010, 0.010, 0.013],
    // The Pavel-demo liveliness: dye blooms and fades within a couple
    // of seconds, velocity lingers so swirls keep carrying, curl high
    // enough that splats roll into vortices instead of smearing.
    dyeDissipation: 0.55,
    velocityDissipation: 0.80,
    curlStrength: 26,
    pressureIterations: 20,
    // Bigger, harder splats than Initiative — a finger flick should
    // throw a bright plume like the reference demo.
    splatRadius: 0.0030,
    splatForce: 6.5,
    emitter: 'bursts',
    emitPeriodSec: 1.3,
    // Silver / white / smoke greys — luminous greyscale on black.
    inks: [
      [0.85, 0.87, 0.92], // bright silver
      [0.52, 0.55, 0.62], // cool grey
      [0.28, 0.30, 0.36], // smoke
      [1.00, 1.00, 1.00], // white-hot pulse
    ],
    touchInk: [0.90, 0.92, 0.97],
    vignette: 0.85,
  },
  'fluid-marbling-light': {
    displayMode: 'subtractive',
    bg: [0.98, 0.98, 0.98],
    // The marbling signature: ink persists. 0.998/s barely fades —
    // trails comb into marble veins and only slowly lift, so the
    // surface "returns to its normal state" over ~20s of calm.
    dyeDissipation: 0.998,
    velocityDissipation: 0.75,
    curlStrength: 22,
    pressureIterations: 22,
    splatRadius: 0.0016,
    splatForce: 5.0,
    emitter: 'sweeps',
    emitPeriodSec: 2.6,
    // Graphite / chrome / warm-grey inks on white — the Electric
    // Light identity (bright pale chrome, dark accents).
    inks: [
      [0.82, 0.80, 0.78], // near-black graphite
      [0.45, 0.42, 0.40], // mid chrome grey
      [0.62, 0.58, 0.50], // warm steel
    ],
    touchInk: [0.82, 0.80, 0.78],
    vignette: 0.55,
  },
};

// ── Simulation ───────────────────────────────────────────────────────

export interface FluidPointerInput {
  /** Pointer position in sim uv space (0..1, y up). */
  x: number;
  y: number;
  /** Movement since last frame in uv space. */
  dx: number;
  dy: number;
  down: boolean;
  /** True when the pointer moved this frame (splat only then). */
  moved: boolean;
}

export interface FluidStepInputs {
  pointer: FluidPointerInput;
  /** 0.2..1 from live charge power — drives emitter cadence. */
  intensity: number;
  /** True while a session is actively charging (or preview active). */
  active: boolean;
  timeSec: number;
}

interface Pass {
  program: WebGLProgram;
}

/** Deterministic per-instance pseudo-random (no Math.random at module
 *  scope; instances seed from construction time so two previews don't
 *  pulse in lockstep). */
function makeRng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5; s >>>= 0;
    return s / 0xffffffff;
  };
}

export class FluidSim {
  private readonly gl: WebGL2RenderingContext;
  private readonly cfg: FluidVariantConfig;
  private readonly quad: WebGLBuffer;

  private velocity: SimDoubleTarget;
  private dye: SimDoubleTarget;
  private pressure: SimDoubleTarget;
  private divergence: SimTarget;
  private curl: SimTarget;

  private advection: Pass;
  private divergencePass: Pass;
  private curlPass: Pass;
  private vorticityPass: Pass;
  private pressurePass: Pass;
  private gradientPass: Pass;
  private splatPass: Pass;
  private displayPass: Pass;

  private readonly rng: () => number;
  private nextEmitAt = 0;
  private emitInkIndex = 0;
  private timeSec = 0;
  /** Sweep emitter state: a moving source travelling across the
   *  surface while charging (the marbling pulse). */
  private sweep: { active: boolean; x: number; y: number; dirX: number; dirY: number; ink: number } = {
    active: false, x: 0, y: 0, dirX: 0, dirY: 0, ink: 0,
  };

  private constructor(
    gl: WebGL2RenderingContext,
    cfg: FluidVariantConfig,
    quad: WebGLBuffer,
    targets: {
      velocity: SimDoubleTarget;
      dye: SimDoubleTarget;
      pressure: SimDoubleTarget;
      divergence: SimTarget;
      curl: SimTarget;
    },
    passes: {
      advection: Pass; divergencePass: Pass; curlPass: Pass; vorticityPass: Pass;
      pressurePass: Pass; gradientPass: Pass; splatPass: Pass; displayPass: Pass;
    },
    seed: number,
  ) {
    this.gl = gl;
    this.cfg = cfg;
    this.quad = quad;
    this.velocity = targets.velocity;
    this.dye = targets.dye;
    this.pressure = targets.pressure;
    this.divergence = targets.divergence;
    this.curl = targets.curl;
    this.advection = passes.advection;
    this.divergencePass = passes.divergencePass;
    this.curlPass = passes.curlPass;
    this.vorticityPass = passes.vorticityPass;
    this.pressurePass = passes.pressurePass;
    this.gradientPass = passes.gradientPass;
    this.splatPass = passes.splatPass;
    this.displayPass = passes.displayPass;
    this.rng = makeRng(seed);
  }

  /**
   * Build the sim against a live context. Returns null on any
   * compile/allocation failure (already console-logged downstream) —
   * the host renders its static fallback.
   *
   * Resolution scales with the canvas: dye at up to 512, velocity at
   * a quarter of dye. Small preview tiles allocate proportionally
   * tiny textures, which is what lets several sims share a phone GPU.
   */
  static create(
    gl: WebGL2RenderingContext,
    variant: FluidVariantId,
    canvasSize: number,
    seed: number,
  ): FluidSim | null {
    const cfg = FLUID_VARIANTS[variant];
    const formats = resolveSimFormats(gl);

    const dyeRes = Math.max(96, Math.min(512, Math.round(canvasSize)));
    const simRes = Math.max(48, Math.round(dyeRes / 4));

    const quad = gl.createBuffer();
    if (!quad) return null;
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );

    const mk = (key: string, frag: string): Pass | null => {
      const program = getOrCreateProgram(gl, key, SIM_VERTEX_SOURCE, frag);
      if (!program) return null;
      setupSimQuad(gl, program, quad);
      return { program };
    };

    const displayFrag = cfg.displayMode === 'subtractive'
      ? DISPLAY_FRAG_TEMPLATE.replace(
        'precision highp float;',
        'precision highp float;\n#define DISPLAY_SUBTRACTIVE 1',
      )
      : DISPLAY_FRAG_TEMPLATE;

    const advection = mk('sim:fluid:advection', ADVECTION_FRAG);
    const divergencePass = mk('sim:fluid:divergence', DIVERGENCE_FRAG);
    const curlPass = mk('sim:fluid:curl', CURL_FRAG);
    const vorticityPass = mk('sim:fluid:vorticity', VORTICITY_FRAG);
    const pressurePass = mk('sim:fluid:pressure', PRESSURE_FRAG);
    const gradientPass = mk('sim:fluid:gradient', GRADIENT_SUBTRACT_FRAG);
    const splatPass = mk('sim:fluid:splat', SPLAT_FRAG);
    const displayPass = mk(`sim:fluid:display:${cfg.displayMode}`, displayFrag);
    if (!advection || !divergencePass || !curlPass || !vorticityPass
      || !pressurePass || !gradientPass || !splatPass || !displayPass) {
      gl.deleteBuffer(quad);
      return null;
    }

    const velocity = createSimDoubleTarget(gl, simRes, simRes, formats);
    const dye = createSimDoubleTarget(gl, dyeRes, dyeRes, formats);
    const pressure = createSimDoubleTarget(gl, simRes, simRes, formats, gl.NEAREST);
    const divergence = createSimTarget(gl, simRes, simRes, formats, gl.NEAREST);
    const curlT = createSimTarget(gl, simRes, simRes, formats, gl.NEAREST);
    if (!velocity || !dye || !pressure || !divergence || !curlT) {
      if (velocity) disposeSimDoubleTarget(gl, velocity);
      if (dye) disposeSimDoubleTarget(gl, dye);
      if (pressure) disposeSimDoubleTarget(gl, pressure);
      if (divergence) disposeSimTarget(gl, divergence);
      if (curlT) disposeSimTarget(gl, curlT);
      gl.deleteBuffer(quad);
      return null;
    }

    return new FluidSim(
      gl, cfg, quad,
      { velocity, dye, pressure, divergence, curl: curlT },
      { advection, divergencePass, curlPass, vorticityPass, pressurePass, gradientPass, splatPass, displayPass },
      seed,
    );
  }

  dispose(): void {
    const { gl } = this;
    disposeSimDoubleTarget(gl, this.velocity);
    disposeSimDoubleTarget(gl, this.dye);
    disposeSimDoubleTarget(gl, this.pressure);
    disposeSimTarget(gl, this.divergence);
    disposeSimTarget(gl, this.curl);
    gl.deleteBuffer(this.quad);
    // Programs stay in glContext's per-context cache on purpose —
    // same lifecycle as the single-pass shaders.
  }

  // ── Splats ─────────────────────────────────────────────────────

  private splat(
    x: number,
    y: number,
    velX: number,
    velY: number,
    ink: readonly [number, number, number],
    radiusScale: number,
    inkScale: number,
  ): void {
    const { gl, cfg } = this;
    const p = this.splatPass.program;
    gl.useProgram(p);
    this.bindQuad(p);
    const radius = cfg.splatRadius * radiusScale;

    // Velocity splat.
    gl.uniform1i(gl.getUniformLocation(p, 'uTarget'), bindTexture(gl, this.velocity.read.texture, 0));
    gl.uniform1f(gl.getUniformLocation(p, 'uAspect'), 1.0);
    gl.uniform2f(gl.getUniformLocation(p, 'uPoint'), x, y);
    gl.uniform3f(gl.getUniformLocation(p, 'uColor'), velX, velY, 0);
    gl.uniform1f(gl.getUniformLocation(p, 'uRadius'), radius);
    blitTo(gl, this.velocity.write);
    this.velocity.swap();

    // Dye splat.
    gl.uniform1i(gl.getUniformLocation(p, 'uTarget'), bindTexture(gl, this.dye.read.texture, 0));
    gl.uniform2f(gl.getUniformLocation(p, 'uPoint'), x, y);
    gl.uniform3f(
      gl.getUniformLocation(p, 'uColor'),
      ink[0] * inkScale, ink[1] * inkScale, ink[2] * inkScale,
    );
    gl.uniform1f(gl.getUniformLocation(p, 'uRadius'), radius);
    blitTo(gl, this.dye.write);
    this.dye.swap();
  }

  private bindQuad(program: WebGLProgram): void {
    setupSimQuad(this.gl, program, this.quad);
  }

  // ── Emitters ───────────────────────────────────────────────────

  /** Initiative: pulsing green light bursts while untouched. Cadence
   *  tightens and bursts brighten as charge power rises. */
  private emitBursts(inputs: FluidStepInputs): void {
    if (inputs.timeSec < this.nextEmitAt) return;
    const period = this.cfg.emitPeriodSec / (0.55 + inputs.intensity);
    this.nextEmitAt = inputs.timeSec + period * (0.7 + this.rng() * 0.6);

    const x = 0.18 + this.rng() * 0.64;
    const y = 0.18 + this.rng() * 0.64;
    const ink = this.cfg.inks[this.emitInkIndex % this.cfg.inks.length]!;
    this.emitInkIndex += 1;
    const strength = 0.5 + inputs.intensity * 0.9;
    // A burst is dye + a soft omni-directional shove: four weak
    // velocity splats around the centre so the light blooms outward.
    this.splat(x, y, 0, 0, ink, 5.0 * strength, 0.9 * strength);
    const push = 1.6 * strength;
    this.splat(x + 0.015, y, push, 0, [0, 0, 0], 2.2, 0);
    this.splat(x - 0.015, y, -push, 0, [0, 0, 0], 2.2, 0);
    this.splat(x, y + 0.015, 0, push, [0, 0, 0], 2.2, 0);
    this.splat(x, y - 0.015, 0, -push, [0, 0, 0], 2.2, 0);
  }

  /** Electric Light: while charging, an ink source sweeps across the
   *  paper leaving a combed marble trail, then rests. */
  private emitSweeps(inputs: FluidStepInputs, dt: number): void {
    if (!this.sweep.active) {
      if (!inputs.active || inputs.timeSec < this.nextEmitAt) return;
      // Launch a sweep from one edge toward the other, slight diagonal.
      const fromLeft = this.rng() > 0.5;
      const y = 0.25 + this.rng() * 0.5;
      this.sweep = {
        active: true,
        x: fromLeft ? 0.04 : 0.96,
        y,
        dirX: (fromLeft ? 1 : -1) * (0.16 + inputs.intensity * 0.2),
        dirY: (this.rng() - 0.5) * 0.12,
        ink: this.emitInkIndex % this.cfg.inks.length,
      };
      this.emitInkIndex += 1;
      return;
    }
    // Advance the travelling source; splat ink + directional velocity
    // continuously so the trail combs into the flow behind it.
    const s = this.sweep;
    s.x += s.dirX * dt;
    s.y += s.dirY * dt + Math.sin(inputs.timeSec * 2.4) * 0.05 * dt;
    const ink = this.cfg.inks[s.ink]!;
    this.splat(s.x, s.y, s.dirX * 2.4, s.dirY * 2.4, ink, 1.4, 0.55);
    if (s.x < 0.02 || s.x > 0.98 || s.y < 0.05 || s.y > 0.95) {
      s.active = false;
      const period = this.cfg.emitPeriodSec / (0.55 + inputs.intensity);
      this.nextEmitAt = inputs.timeSec + period * (0.8 + this.rng() * 0.5);
    }
  }

  /** Gentle traversing impulses keep the contour terrain alive while charging. */
  private emitTerrain(inputs: FluidStepInputs): void {
    if(inputs.timeSec<this.nextEmitAt) return;
    this.nextEmitAt=inputs.timeSec+this.cfg.emitPeriodSec/(0.65+inputs.intensity);
    const x=0.12+((this.emitInkIndex*0.23)%0.76);
    const y=0.35+Math.sin(x*5+0.3)*0.13+this.rng()*0.12;
    const ink=this.cfg.inks[this.emitInkIndex%this.cfg.inks.length]!;
    this.emitInkIndex++;
    this.splat(x,y,0.12,0.08,ink,2.5,0.28+inputs.intensity*0.20);
  }

  // ── Frame ──────────────────────────────────────────────────────

  step(dt: number, inputs: FluidStepInputs): void {
    const { gl, cfg } = this;
    this.timeSec=inputs.timeSec;
    gl.disable(gl.BLEND);

    // 1. Interaction splats — the finger IS the reference fluid demo.
    const ptr = inputs.pointer;
    if (ptr.down && ptr.moved) {
      const velX = ptr.dx * cfg.splatForce / Math.max(dt, 1 / 120);
      const velY = ptr.dy * cfg.splatForce / Math.max(dt, 1 / 120);
      // Cap the shove so a fast flick stays pretty instead of tearing.
      const mag = Math.hypot(velX, velY);
      const cap = 9.0;
      const k = mag > cap ? cap / mag : 1;
      this.splat(ptr.x, ptr.y, velX * k, velY * k, cfg.touchInk, 1.0, 0.5);
    } else if (cfg.emitter === 'bursts') {
      // Idle pulses only when the finger is off the surface.
      this.emitBursts(inputs);
    }
    if (cfg.emitter === 'sweeps') this.emitSweeps(inputs, dt);
    if (cfg.emitter === 'terrain' && !ptr.down) this.emitTerrain(inputs);

    // 2. Curl + vorticity confinement (keeps small swirls alive).
    let p = this.curlPass.program;
    gl.useProgram(p);
    this.bindQuad(p);
    gl.uniform1i(gl.getUniformLocation(p, 'uVelocity'), bindTexture(gl, this.velocity.read.texture, 0));
    gl.uniform2f(gl.getUniformLocation(p, 'uTexelSize'), this.velocity.texelSize[0], this.velocity.texelSize[1]);
    blitTo(gl, this.curl);

    p = this.vorticityPass.program;
    gl.useProgram(p);
    this.bindQuad(p);
    gl.uniform1i(gl.getUniformLocation(p, 'uVelocity'), bindTexture(gl, this.velocity.read.texture, 0));
    gl.uniform1i(gl.getUniformLocation(p, 'uCurl'), bindTexture(gl, this.curl.texture, 1));
    gl.uniform2f(gl.getUniformLocation(p, 'uTexelSize'), this.velocity.texelSize[0], this.velocity.texelSize[1]);
    gl.uniform1f(gl.getUniformLocation(p, 'uCurlStrength'), cfg.curlStrength);
    gl.uniform1f(gl.getUniformLocation(p, 'uDt'), dt);
    // Bound the new long-running vortices so accumulated velocity cannot
    // wash all dye off the surface. Existing simulation tuning stays intact.
    gl.uniform1f(gl.getUniformLocation(p, 'uVelocityCap'), cfg.personality ? 2.5 : 1000.0);
    blitTo(gl, this.velocity.write);
    this.velocity.swap();

    // 3. Projection: divergence → pressure Jacobi → subtract gradient.
    p = this.divergencePass.program;
    gl.useProgram(p);
    this.bindQuad(p);
    gl.uniform1i(gl.getUniformLocation(p, 'uVelocity'), bindTexture(gl, this.velocity.read.texture, 0));
    gl.uniform2f(gl.getUniformLocation(p, 'uTexelSize'), this.velocity.texelSize[0], this.velocity.texelSize[1]);
    blitTo(gl, this.divergence);

    p = this.pressurePass.program;
    gl.useProgram(p);
    this.bindQuad(p);
    gl.uniform2f(gl.getUniformLocation(p, 'uTexelSize'), this.velocity.texelSize[0], this.velocity.texelSize[1]);
    gl.uniform1i(gl.getUniformLocation(p, 'uDivergence'), bindTexture(gl, this.divergence.texture, 1));
    const uPressureLoc = gl.getUniformLocation(p, 'uPressure');
    for (let i = 0; i < cfg.pressureIterations; i += 1) {
      gl.uniform1i(uPressureLoc, bindTexture(gl, this.pressure.read.texture, 0));
      blitTo(gl, this.pressure.write);
      this.pressure.swap();
    }

    p = this.gradientPass.program;
    gl.useProgram(p);
    this.bindQuad(p);
    gl.uniform1i(gl.getUniformLocation(p, 'uPressure'), bindTexture(gl, this.pressure.read.texture, 0));
    gl.uniform1i(gl.getUniformLocation(p, 'uVelocity'), bindTexture(gl, this.velocity.read.texture, 1));
    gl.uniform2f(gl.getUniformLocation(p, 'uTexelSize'), this.velocity.texelSize[0], this.velocity.texelSize[1]);
    blitTo(gl, this.velocity.write);
    this.velocity.swap();

    // 4. Advect velocity, then dye. Dissipation is exponential-ish
    // per frame: raise the per-second config to the dt power so the
    // fade rate is frame-rate independent under the 30fps cap.
    p = this.advection.program;
    gl.useProgram(p);
    this.bindQuad(p);
    const velTex = bindTexture(gl, this.velocity.read.texture, 0);
    gl.uniform1i(gl.getUniformLocation(p, 'uVelocity'), velTex);
    gl.uniform1i(gl.getUniformLocation(p, 'uSource'), velTex);
    gl.uniform1f(gl.getUniformLocation(p, 'uDt'), dt);
    gl.uniform1f(gl.getUniformLocation(p, 'uDissipation'), Math.pow(cfg.velocityDissipation, dt));
    blitTo(gl, this.velocity.write);
    this.velocity.swap();

    gl.uniform1i(gl.getUniformLocation(p, 'uVelocity'), bindTexture(gl, this.velocity.read.texture, 0));
    gl.uniform1i(gl.getUniformLocation(p, 'uSource'), bindTexture(gl, this.dye.read.texture, 1));
    gl.uniform1f(gl.getUniformLocation(p, 'uDt'), dt);
    gl.uniform1f(gl.getUniformLocation(p, 'uDissipation'), Math.pow(cfg.dyeDissipation, dt));
    blitTo(gl, this.dye.write);
    this.dye.swap();
  }

  /** Paint the dye to the canvas. */
  render(): void {
    const { gl, cfg } = this;
    const p = this.displayPass.program;
    gl.useProgram(p);
    this.bindQuad(p);
    gl.uniform1i(gl.getUniformLocation(p, 'uDye'), bindTexture(gl, this.dye.read.texture, 0));
    gl.uniform3f(gl.getUniformLocation(p, 'uBg'), cfg.bg[0], cfg.bg[1], cfg.bg[2]);
    gl.uniform1f(gl.getUniformLocation(p, 'uVignette'), cfg.vignette);
    gl.uniform1f(gl.getUniformLocation(p, 'uTime'), this.timeSec);
    gl.uniform1f(gl.getUniformLocation(p, 'uPersonality'), cfg.personality??0);
    gl.uniform1f(gl.getUniformLocation(p, 'uLight'), cfg.displayMode==='subtractive'?1:0);
    blitTo(gl, null);
  }
}
